import { AppError, errorMessage } from "../core/errors";

export interface Capability {
  id: string;
  label: string;
  status: "supported" | "unsupported" | "unknown" | "partial";
  detail: string;
}

export interface AudioOutputInfo { deviceId: string; label: string; }

/** Environment probing for the My Gear capability panel (AGENT.md §16). */
export function probeCapabilities(ctx: AudioContext | null, g: typeof globalThis = globalThis): Capability[] {
  const caps: Capability[] = [];
  const hasAC = typeof g.AudioContext === "function";
  caps.push({ id: "webaudio", label: "Web Audio API", status: hasAC ? "supported" : "unsupported", detail: hasAC ? "AudioContext available." : "This browser cannot produce audio with Web Audio." });
  const hasWorklet = typeof g.AudioWorkletNode === "function";
  caps.push({ id: "worklet", label: "AudioWorklet", status: hasWorklet ? "supported" : "unsupported", detail: hasWorklet ? "Custom DSP on the audio thread is possible." : "Custom DSP features will be unavailable." });
  const nav = (g as { navigator?: Navigator }).navigator;
  const hasMidi = !!nav && "requestMIDIAccess" in nav;
  caps.push({ id: "midi", label: "Web MIDI API", status: hasMidi ? "supported" : "unsupported", detail: hasMidi ? "MIDI devices can be used after permission is granted (Firefox shows a site-permission add-on prompt)." : "No Web MIDI (Safari/iOS do not implement it). On-screen and computer-keyboard input still work." });
  const sink = hasAC && "setSinkId" in g.AudioContext.prototype;
  caps.push({ id: "sinkid", label: "Output device selection (AudioContext.setSinkId)", status: sink ? "supported" : "unsupported", detail: sink ? "You can choose the physical output device." : "Output device follows the operating-system default (Chromium-only API)." });
  const secure = g.isSecureContext !== false;
  caps.push({ id: "secure", label: "Secure context", status: secure ? "supported" : "unsupported", detail: secure ? "HTTPS/localhost — device APIs allowed." : "Device APIs (MIDI, output selection) need HTTPS." });
  const persist = !!nav?.storage && typeof nav.storage.persist === "function";
  const session = !!nav && "audioSession" in nav;
  caps.push({ id: "audiosession", label: "Audio session (iOS routing)", status: session ? "supported" : "unknown", detail: session ? "Playback is routed to the speaker/headphones, also with the silent switch on." : "Not needed outside Safari; on older iOS the output may follow the silent switch." });
  caps.push({ id: "persist", label: "Persistent storage", status: persist ? "supported" : "unsupported", detail: persist ? "Projects can be protected from automatic eviction." : "Browser may evict local projects under storage pressure." });
  if (ctx) {
    const ch = ctx.destination.maxChannelCount;
    caps.push({
      id: "channels", label: "Output channels", status: ch > 2 ? "supported" : "partial",
      detail: `Current device reports ${ch} channel${ch === 1 ? "" : "s"} (maxChannelCount). ${ch > 2 ? "Multichannel routing may be possible — validate with the channel test." : "Stereo only on this device/browser."}`,
    });
    const lat = [`sample rate ${ctx.sampleRate} Hz`, `base latency ${fmtMs(ctx.baseLatency)}`];
    if ("outputLatency" in ctx) lat.push(`output latency ${fmtMs(ctx.outputLatency)}`);
    caps.push({ id: "latency", label: "Audio timing", status: "supported", detail: lat.join(", ") });
  }
  return caps;
}

function fmtMs(sec: number | undefined): string {
  return typeof sec === "number" && Number.isFinite(sec) ? `${(sec * 1000).toFixed(1)} ms` : "unknown";
}

export function supportsSinkSelection(ctx: AudioContext | null): boolean {
  return !!ctx && typeof (ctx as AudioContext & { setSinkId?: unknown }).setSinkId === "function";
}

/** List audio outputs. Labels may be empty until the browser grants a media permission. */
export async function listAudioOutputs(md: MediaDevices | undefined = globalThis.navigator?.mediaDevices): Promise<AudioOutputInfo[]> {
  if (!md?.enumerateDevices) return [];
  const all = await md.enumerateDevices();
  return all.filter((d) => d.kind === "audiooutput").map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Output ${i + 1}${d.deviceId === "default" ? " (default)" : ""}` }));
}

export async function setOutputDevice(ctx: AudioContext, deviceId: string): Promise<void> {
  const c = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
  if (typeof c.setSinkId !== "function") throw new AppError("SINK_UNSUPPORTED", "This browser cannot select an output device for Web Audio.");
  try {
    await c.setSinkId(deviceId === "default" ? "" : deviceId);
  } catch (err) {
    throw new AppError("SINK_FAILED", `Could not switch output device: ${errorMessage(err)}`, { cause: err });
  }
}

/**
 * Play a short test tone on one physical output channel (0-based) by opening
 * the destination to its max channel count in discrete mode. Used to validate
 * multichannel interfaces experimentally (TODO R-AUDIO-01). Restores the
 * destination configuration afterwards.
 */
export function playChannelTestTone(ctx: AudioContext, channel: number, durationSec = 0.6): Promise<void> {
  const dest = ctx.destination;
  const max = dest.maxChannelCount;
  if (channel < 0 || channel >= Math.max(max, 1)) return Promise.reject(new RangeError(`Channel ${channel + 1} not available (device reports ${max}).`));
  const prev = { count: dest.channelCount, mode: dest.channelCountMode, interp: dest.channelInterpretation };
  dest.channelCount = max;
  dest.channelCountMode = "explicit";
  dest.channelInterpretation = "discrete";
  const merger = ctx.createChannelMerger(max);
  const osc = ctx.createOscillator();
  osc.frequency.value = 440 + channel * 110;
  const g = ctx.createGain();
  const t = ctx.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.25, t + 0.02);
  g.gain.setValueAtTime(0.25, t + durationSec - 0.05);
  g.gain.linearRampToValueAtTime(0, t + durationSec);
  osc.connect(g).connect(merger, 0, channel);
  merger.connect(dest);
  osc.start(t);
  osc.stop(t + durationSec);
  return new Promise((resolve) => {
    osc.onended = () => {
      osc.disconnect(); g.disconnect(); merger.disconnect();
      dest.channelCount = prev.count;
      dest.channelCountMode = prev.mode;
      dest.channelInterpretation = prev.interp;
      resolve();
    };
  });
}
